import {Component, ElementRef, OnInit, signal} from '@angular/core';
import {TaAppStore} from '../../../state/clcta-store';
import {Card} from 'primeng/card';
import {TableModule} from 'primeng/table';
import {AdminService, AtoTier} from '../../../api';
import {LoadingService} from '../../../services/loading.service';
import {Select} from 'primeng/select';
import {FormsModule} from '@angular/forms';
import {NgClass} from '@angular/common';
import {Button} from 'primeng/button';
import {InputNumber} from 'primeng/inputnumber';
import {toLocationCategoriesText} from '../../../util/app.types';

@Component({
  selector: 'clc-ato-rates-admin',
  imports: [
    Card,
    TableModule,
    Select,
    FormsModule,
    Button,
    InputNumber,
    NgClass
  ],
  templateUrl: './clc-ato-rates-admin.html',
  styleUrl: './clc-ato-rates-admin.scss'
})
export class ClcAtoRatesAdmin implements OnInit {
  atoRateTiersList: AtoTier[] = [];
  selectedRateTier: AtoTier | undefined;
  localRateTier: AtoTier | undefined;
  localRateTierChanged = signal<boolean>(false);

  constructor(
    private store: TaAppStore,
    private loadingService: LoadingService,
    private adminApi: AdminService,
  ) {}

  ngOnInit(): void {
    this.loadRates();
    this.store.updateBreadCrumbData({
      title: 'CLC Tiers',
      titleIcon: 'fa-regular fa-chart-area fa-lg'
    });
  }

  loadRates(): void {
    this.loadingService.showLoading({message: 'Loading CLC Tiers'});

    this.adminApi.getAtoRates().subscribe({
      next: resp => {
        if (resp.data && resp.data.length > 0) {
          resp.data.forEach(tier => this.sortTierRates(tier));
          this.atoRateTiersList = resp.data;
          this.setSelectedRateTier(resp.data.at(0))
        } else {
          console.error('No data');
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error(err);
        this.loadingService.hideLoading();
      }
    });
  }

  setSelectedRateTier(rateTier: AtoTier | undefined) {
    if (rateTier !== undefined) {
      this.selectedRateTier = rateTier;
      this.localRateTier = {...rateTier, rates: rateTier.rates?.map(x => ({...x})) ?? [] };
    }
    this.localRateTierChanged.set(!this.areRatesEqual());
  }

  onRateChange(): void {
    this.localRateTierChanged.set(!this.areRatesEqual());
  }

  areRatesEqual(): boolean {
    if (!this.selectedRateTier?.rates && !this.localRateTier?.rates ) return true;
    if (!this.selectedRateTier?.rates) return false;
    if (!this.localRateTier?.rates) return false;

    for (let ii = 0; ii < this.selectedRateTier.rates!.length; ii++) {
      if (this.selectedRateTier.rates![ii].rate !== this.localRateTier.rates![ii].rate) {
        return false;
      }
    }
    return true;
  }

  saveSelectedRateChanges() {
    this.loadingService.showLoading({message: 'Loading CLC Tiers'});

    if (this.localRateTier !== undefined) {
      this.adminApi.updateAtoTier(this.localRateTier).subscribe({
        next: result => {
          if (result.success && result.data) {
            this.sortTierRates(result.data);
            const index = this.atoRateTiersList.findIndex((tier) => tier.id === result.data?.id);
            if (index >= 0) {
              this.atoRateTiersList[index] = result.data;
            }
            this.setSelectedRateTier(result.data);
          }
          this.loadingService.hideLoading();
        },
        error: err => {
          console.error(err);
          this.loadingService.hideLoading();
        }
      });
    }
  }

  //Sorts based on category first, then on name with order: breakfast -> lunch -> dinner -> incidentals -> alphabetical
  private sortTierRates(tier: AtoTier): void {
    const nameOrder = ['Breakfast', 'Lunch', 'Dinner', 'Incidentals'];
    tier.rates?.sort((a, b) => {
      const categoryComp = (a.category ?? '').localeCompare(b.category ?? '');
      if (categoryComp !== 0) return categoryComp;
      const aIdx = nameOrder.indexOf(a.name ?? '');
      const bIdx = nameOrder.indexOf(b.name ?? '');
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }

  cancelSelectedRateTierChanges(): void {
    if (this.selectedRateTier !== undefined) {
      this.localRateTier = {...this.selectedRateTier, rates: this.selectedRateTier.rates?.map(x => ({...x})) ?? [] };
      this.localRateTierChanged.set(!this.areRatesEqual());
    }
  }

  protected readonly toLocationCategoriesText = toLocationCategoriesText;
}
